/* @unocss-include */
import type { Message } from '~/types/chat';
import type { UserData } from '~/types/user';
import { CC_MAGIC_STRING } from '~/utils/constants';

export const AGENTS = {
  ClaudeCode: 'claude-code',
  Codex: 'codex',
  Bolt: 'bolt',
} as const;

export type SupportedAgents = (typeof AGENTS)[keyof typeof AGENTS];

interface AgentOption {
  value: SupportedAgents;
  label: string;
  icon: string;
  disabled?: boolean;
  apiUrl: string;
  colorVariable?: string;
  description?: string;
  properties?: AgentProperty[];
  maxSteps: number;
}

interface AgentProperty {
  label: string;

  /**
   * @description Value between 0-5
   */
  value: number;
}

export const AGENT_MAP = new Map<SupportedAgents, AgentOption>([
  [
    AGENTS.ClaudeCode,
    {
      value: AGENTS.ClaudeCode,
      label: 'Claude Agent',
      icon: 'i-bolt:logos-claude',
      apiUrl: '/api/chat/v2',
      colorVariable: '[--agent-color:#D97757]',
      description: 'Advanced agent developed by Anthropic, ideal for working with larger codebases.',
      maxSteps: 1,
      properties: [
        {
          label: 'Speed',
          value: 2,
        },
        {
          label: 'Context management',
          value: 5,
        },
      ],
    },
  ],
  [
    AGENTS.Codex,
    {
      value: AGENTS.Codex,
      label: 'Codex',
      icon: 'i-bolt:logos-codex',
      disabled: true,
      apiUrl: '/api/chat/v2',
      maxSteps: 1,
    },
  ],
  [
    AGENTS.Bolt,
    {
      value: AGENTS.Bolt,
      label: 'v1 Agent (legacy)',
      icon: 'i-ph:sun-horizon',
      apiUrl: '/api/chat',
      description: 'Fast agent, ideal for getting initial bootstrapping done.',
      maxSteps: 2,
      properties: [
        {
          label: 'Speed',
          value: 4,
        },
        {
          label: 'Context management',
          value: 2,
        },
      ],
    },
  ],
]);

export const AGENT_OPTIONS = Array.from(AGENT_MAP.values());

export function getAgentName(agent: SupportedAgents, options?: { removeLegacy?: boolean }) {
  let name = AGENT_MAP.get(agent)?.label;

  if (options?.removeLegacy) {
    name = name?.replace(' (legacy)', '');
  }

  return name;
}

export function getAgentIcon(agent: SupportedAgents) {
  return AGENT_MAP.get(agent)?.icon;
}

export function getAgentApiUrl(agent?: SupportedAgents) {
  return AGENT_MAP.get(agent ?? getDefaultAgent())?.apiUrl;
}

export function getAgentColorVariable(agent?: SupportedAgents) {
  return AGENT_MAP.get(agent ?? getDefaultAgent())?.colorVariable;
}

export function getAgentProperties(agent?: SupportedAgents) {
  return AGENT_MAP.get(agent ?? getDefaultAgent())?.properties ?? [];
}

export function getAgentDescription(agent?: SupportedAgents) {
  return AGENT_MAP.get(agent ?? getDefaultAgent())?.description;
}

export function getMaxSteps(agent?: SupportedAgents) {
  return AGENT_MAP.get(agent ?? getDefaultAgent())?.maxSteps ?? 1;
}

export function getDefaultAgent(user?: UserData) {
  return userCanUseClaudeCodeAgent(user) ? AGENTS.ClaudeCode : AGENTS.Bolt;
}

export function userCanUseClaudeCodeAgent(user?: UserData) {
  return user?.featureFlags.claudeCode || (import.meta.env.DEV && import.meta.env.VITE_USE_CLAUDE_CODE_AGENT);
}

export function isClaudeCodeAgent(agent?: SupportedAgents) {
  return agent === AGENTS.ClaudeCode;
}

export function isBoltAgent(agent?: SupportedAgents) {
  return agent === AGENTS.Bolt;
}

export function isClaudeCodeMessage(message: Message) {
  return message.content.startsWith(CC_MAGIC_STRING);
}

export function isBoltMessage(message: Message) {
  return !isClaudeCodeMessage(message);
}
